import React from 'react';
import { AUDIT_REPORT_CAMPAIGN, isAuditCampaignActive } from '../../utils/auditCampaign';
import { Box } from '@mui/material';
import { NavLink, Outlet } from 'react-router-dom';
import { H4 } from '../../components/typography';
import Spacer from '../../components/Spacer';
import HyriaLogo from '../../assets/hyria-logo.svg';
import { PieChart, User, ChevronDown, BarChart3, FileText, Users, Download, Home, UsersIcon, FileSearch } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { signOut } from 'firebase/auth';
import { auth, db } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';

const navItems = [
  { label: 'Översikt', path: '', icon: BarChart3 },
  { label: 'Ansökningar', path: 'applications', icon: FileText },
  { label: 'Medlemsförteckning', path: 'units', icon: User },
  { label: 'Avvikelserapport', path: 'audit-report', icon: FileSearch },
  { label: 'Export', path: 'export-import', icon: Download },
];

const mockKPI = [
  { label: 'Nya ansökningar (7 dagar)', value: 3, icon: 'Mail' },
  { label: 'Väntar på beslut', value: 5, icon: 'Clock' },
  { label: 'Pågående uthyrningar', value: 12, icon: 'Home' },
  { label: 'Kontrakt utgår (30 dagar)', value: 2, icon: 'Calendar' },
];

const mockLatest = [
  { name: 'Mohammad AbdelAal', apt: 'Majvivsgatan 1A, 1001', status: 'PENDING', date: '2024-06-01' },
  { name: 'Sara Lindberg', apt: 'Tulpanvägen 3B, 2002', status: 'APPROVED', date: '2024-05-29' },
  { name: 'Anna Svensson', apt: 'Rosenvägen 2C, 1002', status: 'PENDING', date: '2024-05-28' },
];

const mockExpiring = [
  { name: 'Sara Lindberg', apt: 'Tulpanvägen 3B, 2002', end: '2024-06-15' },
  { name: 'Anna Svensson', apt: 'Rosenvägen 2C, 1002', end: '2024-06-20' },
];

function UserMenu() {
  const [user, setUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      setUser(u);
      if (u) {
        setIsLoadingUserData(true);
        try {
          const userRef = doc(db, 'users', u.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) setUserInfo(userSnap.data());
        } catch (error) {
        } finally {
          setIsLoadingUserData(false);
        }
      } else {
        setIsLoadingUserData(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/sign-in');
  };

  if (!user) return null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="absolute top-6 right-8 flex items-center gap-2 bg-white rounded-full shadow-md px-3 py-2 hover:bg-zinc-50 transition focus:outline-none">
          <User className="w-6 h-6 text-[#2563eb]" />
          <span className="hidden md:block text-zinc-700 font-medium text-sm">
            {isLoadingUserData 
              ? 'Laddar...' 
              : userInfo?.firstName 
                ? `${userInfo.firstName} ${userInfo.lastName}` 
                : user.email
            }
          </span>
          <ChevronDown className="w-4 h-4 text-zinc-500" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content sideOffset={8} className="bg-white rounded-xl shadow-lg p-2 min-w-[180px] z-50">
        <DropdownMenu.Item
          className="px-4 py-2 rounded-lg text-zinc-700 hover:bg-zinc-100 cursor-pointer text-sm"
          onClick={() => navigate('/dashboard/settings')}
        >
          Inställningar
        </DropdownMenu.Item>
        <DropdownMenu.Separator className="my-1 h-px bg-zinc-100" />
        <DropdownMenu.Item
          className="px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer text-sm"
          onClick={handleLogout}
        >
          Logga ut
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default function DashboardLayout() {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [auditUnreadCount, setAuditUnreadCount] = useState(0);
  // Under kampanjen lyser "NY" på Avvikelserapport hos alla föreningar,
  // med "(Gratis)" i etiketten. Annars bara när en färdig rapport ligger oläst.
  const campaignActive = isAuditCampaignActive();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setUserInfo(userData);

          // Hämta unread audit report orders
          if (userData.associationId) {
            try {
              const ordersSnap = await getDocs(query(
                collection(db, 'auditReportOrders'),
                where('associationId', '==', userData.associationId)
              ));
              const unread = ordersSnap.docs.filter(d => d.data().unreadBy?.[user.uid] === true).length;
              setAuditUnreadCount(unread);
            } catch (e) {
              // Ignore — collection might not exist yet
            }
          }
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box className="flex min-h-screen bg-gray-50 font-sans items-center justify-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#62B8E9] mx-auto mb-4"></div>
          <p className="text-gray-600">Laddar...</p>
        </div>
      </Box>
    );
  }

  return (
    <Box className="flex min-h-screen bg-gray-50 font-sans" style={{ fontFamily: 'Poppins, sans-serif' }}>
      {/* Sidebar */}
      <Box className="w-72 bg-white shadow-lg rounded-r-2xl flex flex-col p-8 border-r border-gray-200">
        <Box className="flex flex-col items-center mb-8">
          <img src={HyriaLogo} alt="Hyria logo" className="h-12 w-auto mb-2" />
          <H4 className="font-bold tracking-tight text-[#62B8E9] text-2xl mt-2">
            {userInfo?.housingCooperative || 'BRF Admin'}
          </H4>
        </Box>
        <nav className="flex flex-col gap-2 mt-4">
          {navItems.map(item => {
            const IconComponent = item.icon;
            const isAudit = item.path === 'audit-report';
            const showNew = isAudit && (campaignActive || auditUnreadCount > 0);
            return (
              <NavLink
                key={item.path}
                to={item.path === '' ? '/dashboard' : `/dashboard/${item.path}`}
                className={({ isActive }) =>
                  `px-6 py-3 rounded-2xl font-medium transition-all text-base flex items-center gap-4 min-h-[52px]
                  ${isActive
                    ? 'bg-[#62B8E9] text-white font-bold shadow-md'
                    : 'text-zinc-500 hover:bg-[#62B8E9]/10 hover:text-black'}`
                }
                end
              >
                {IconComponent ? <IconComponent className="w-6 h-6 flex-shrink-0" /> : <div className="w-6 h-6 bg-red-500 rounded flex-shrink-0"></div>}
                {/* Etiketten får krympa och radbrytas; pluppen ligger på egen rad
                    under så att den aldrig hamnar utanför menyraden. */}
                <span className="min-w-0 flex-1">
                  <span className="block">{item.label}</span>
                  {showNew && (
                    <span className="mt-1 flex items-center gap-2 text-xs">
                      {campaignActive && <span className="font-semibold">({AUDIT_REPORT_CAMPAIGN.navLabel})</span>}
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                        NY
                      </span>
                    </span>
                  )}
                </span>
              </NavLink>
            );
          })}
        </nav>
        <Spacer spacing={8} />
      </Box>
      <UserMenu />
      {/* Main Content */}
      <Box className="flex-1 p-8 bg-gray-50">
        <Outlet />
      </Box>
    </Box>
  );
} 